import type React from "react";
import classNames from "classnames";
import {
    KickLogo,
    type KickLogoShape,
    type KickLogoSize,
} from "@design-system/KickLogo/KickLogo";
import { kickLogoAnatomyCombination } from "./componentAnatomyKickLogo";
import { Part, type AnatomyPartHighlight } from "./componentAnatomyParts";
import { kickLogoFormatProps } from "./KickLogoPlayground.lib";
import viewStyles from "./ComponentAnatomyView.module.scss";
import styles from "./ComponentAnatomySchematicKickLogo.module.scss";

/** The playground's size, so the x-ray draws the same logo as the live stage. */
export interface KickLogoSchematicContent {
    size: KickLogoSize;
}

const VIEWBOX_HEIGHT = 48;
const FULL_VIEWBOX_WIDTH = 150;

/** A part's bounding box in viewBox units, from the Figma vectors. */
interface VectorBox {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

const tileBox: VectorBox = { left: 0, top: 0, right: 48, bottom: 48 };

const markBoxes: Record<KickLogoShape, VectorBox> = {
    square: { left: 9.68, top: 12.9, right: 37.55, bottom: 35.09 },
    circle: { left: 11.47, top: 14.29, right: 35.85, bottom: 33.71 },
};

const wordmarkBox: VectorBox = {
    left: 69.74,
    top: 7.33,
    right: 149.14,
    bottom: 39.48,
};

const tileRadii: Record<KickLogoShape, string> = {
    square: "12.5%",
    circle: "50%",
};

const toPercent = (value: number, total: number) => `${(value / total) * 100}%`;

const boxStyle = (
    box: VectorBox,
    viewBoxWidth: number,
): React.CSSProperties => ({
    left: toPercent(box.left, viewBoxWidth),
    top: toPercent(box.top, VIEWBOX_HEIGHT),
    width: toPercent(box.right - box.left, viewBoxWidth),
    height: toPercent(box.bottom - box.top, VIEWBOX_HEIGHT),
});

/**
 * A real `KickLogo` with transparent hover targets over the tile, the mark
 * and the wordmark, placed from the Figma vector boxes. White logos sit on
 * `--bg-inverse`, as they would in the product.
 */
export const KickLogoSchematic = ({
    highlight,
    variantId,
    content = { size: "2xl" },
}: {
    highlight: AnatomyPartHighlight;
    variantId: string;
    content?: KickLogoSchematicContent;
}) => {
    const combination = kickLogoAnatomyCombination(variantId);

    if (combination === undefined) {
        return null;
    }

    const { format, color, transparent } = combination;
    const formatProps = kickLogoFormatProps[format];
    const shape = formatProps.variant === "icon" ? formatProps.shape : "square";
    const viewBoxWidth =
        formatProps.variant === "full" ? FULL_VIEWBOX_WIDTH : VIEWBOX_HEIGHT;

    return (
        <span
            className={classNames(viewStyles.schematic, styles.schematic)}
            data-surface={color === "white" ? "inverse" : "primary"}
            data-variant={formatProps.variant}
        >
            <span className={styles.logo}>
                <KickLogo
                    {...formatProps}
                    color={color}
                    transparent={transparent}
                    size={content.size}
                />
                <Part
                    partId="tile"
                    highlight={highlight}
                    className={styles.overlay}
                    style={{
                        ...boxStyle(tileBox, viewBoxWidth),
                        borderRadius: tileRadii[shape],
                    }}
                />
                <Part
                    partId="mark"
                    highlight={highlight}
                    className={styles.overlay}
                    style={boxStyle(markBoxes[shape], viewBoxWidth)}
                />
                {formatProps.variant === "full" && (
                    <Part
                        partId="wordmark"
                        highlight={highlight}
                        className={styles.overlay}
                        style={boxStyle(wordmarkBox, viewBoxWidth)}
                    />
                )}
            </span>
        </span>
    );
};
